// COMP-VIDEO-PANEL — پنل ویدئو کنار پرونده در میز کار (US-TEL-020 تا 022، 030؛ AP-TEL-01): شروع جلسه از اتاق انتظار، توکن ورود کوتاه‌عمر
// میزبان، ثبت رویدادهای فنی (قطع، وصل، افت کیفیت)، کاهش یک‌طرفه نوع ارتباط به «فقط صدا» یا «تلفنی»، گفتگوی متنی جلسه و پایان جلسه.
//
// **رسانه از این پنل نمی‌گذرد:** صدا و تصویر مستقیم بین مرورگر و سرور رسانه خودمیزبان (DTLS-SRTP) جریان دارد و این کامپوننت تنها نشانی
// سرور و توکن کوتاه‌عمر را می‌گیرد (AC-TEL-020.2؛ INT-VIDEO). توکن در State کامپوننت می‌ماند و هرگز در `localStorage` نوشته یا لاگ نمی‌شود
// (CLAUDE.md §۳، §۱۰) و هیچ ضبطی بی رضایت صریح انجام نمی‌شود (BR-TEL-014؛ بیرون از دامنه این Prompt).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type TelConnectionMode,
  type TelJoinTokenView,
  type TelSessionEventKind,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  ErrorState,
  faDigits,
  JalaliDate,
  LoadingState,
  ReasonDialog,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  COMP_VIDEO_PANEL,
  durationLabel,
  ModeChip,
  RoomStatusChip,
  SessionStatusChip,
  telKeys,
  telNum,
} from './common';

/** کاهش نوع ارتباط یک‌طرفه است: ویدئو ← فقط صدا ← تلفنی (BR-TEL-013؛ PHX-TEL-0303). */
const DOWNGRADES: Record<TelConnectionMode, readonly TelConnectionMode[]> = {
  Video: ['AudioOnly', 'Phone'],
  AudioOnly: ['Phone'],
  Phone: [],
};

const TECHNICAL_EVENTS: readonly TelSessionEventKind[] = ['Disconnected', 'Reconnected', 'QualityDegraded'];

export interface VideoPanelProps {
  /** مواجهه مجازی در حال انجام. */
  encounterId: string;
  /** نوبت مواجهه؛ اتاق مجازی از آن پیدا می‌شود. */
  appointmentId: string;
}

/**
 * پنل ویدئو مواجهه مجازی. اگر نوبت اتاق مجازی نداشته باشد (مثلاً خدمت مجازی نیست) پنل هیچ چیزی نمایش نمی‌دهد،
 * چون میز کار نباید برای مواجهه حضوری اقدام ویدئویی پیشنهاد دهد.
 */
export function VideoPanel({ encounterId, appointmentId }: VideoPanelProps) {
  const { t } = useTranslation('tel');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const canHost = access.accessMode === 'Full' && access.can('tel.session.host');
  const idempotency = useIdempotencyKey();
  const [token, setToken] = useState<TelJoinTokenView | null>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [failing, setFailing] = useState(false);
  const room = useQuery({
    queryKey: telKeys.roomByAppointment(session?.tenantId, appointmentId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/telehealth/rooms/by-appointment/{appointmentId}', {
          params: { path: { appointmentId } },
        }),
      ),
    retry: false,
  });
  const sessionId = started ?? token?.sessionId ?? room.data?.activeSessionId ?? null;
  const virtualSession = useQuery({
    queryKey: telKeys.session(session?.tenantId, sessionId ?? ''),
    enabled: sessionId !== null,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/telehealth/sessions/{id}', { params: { path: { id: sessionId ?? '' } } }),
      ),
  });
  const refresh = () => {
    void room.refetch();
    void virtualSession.refetch();
    void queryClient.invalidateQueries({ queryKey: telKeys.all(session?.tenantId) });
  };
  const hostToken = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions/{id}:host-token', { params: { path: { id } } }),
      ),
    onSuccess: setToken,
  });
  // شروع جلسه، خودش توکن نمی‌دهد (فراداده جلسه برمی‌گرداند)؛ توکن کوتاه‌عمر میزبان بی‌درنگ پس از آن گرفته می‌شود.
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions', {
          body: { roomId: room.data?.roomId ?? '', encounterId },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (view) => {
      setStarted(view.sessionId);
      hostToken.mutate(view.sessionId);
      refresh();
    },
  });
  const event = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: TelSessionEventKind }) =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions/{id}/events', {
          params: { path: { id } },
          body: { kind, role: 'Provider', detail: null },
        }),
      ),
    onSuccess: refresh,
  });
  const downgrade = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: TelConnectionMode }) =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions/{id}:downgrade', {
          params: { path: { id } },
          body: { mode },
        }),
      ),
    onSuccess: refresh,
  });
  const end = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/telehealth/sessions/{id}:end', { params: { path: { id } } })),
    onSuccess: () => {
      setEnding(false);
      setToken(null);
      setStarted(null);
      refresh();
    },
  });
  const failure = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions/{id}:technical-failure', {
          params: { path: { id } },
          body: { note },
        }),
      ),
    onSuccess: () => {
      setFailing(false);
      refresh();
    },
  });
  // نوبت بی اتاق مجازی (خدمت حضوری) یا بی مجوز میزبانی: پنل نمایش داده نمی‌شود.
  if (room.error || !canHost) {
    return null;
  }
  if (room.isPending) {
    return <LoadingState rows={2} />;
  }
  const data = room.data;
  if (!data) {
    return null;
  }
  const live = virtualSession.data;
  const active = live?.status === 'Active';
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-screen-id={COMP_VIDEO_PANEL} data-testid="tel-video-panel">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" component="h3">
          {t('tel:panel.title')}
        </Typography>
        <RoomStatusChip status={data.status} />
        {live ? <SessionStatusChip status={live.status} /> : null}
        {live ? <ModeChip mode={live.mode} /> : null}
        {data.providerLate ? (
          <Chip size="small" color="warning" label={t('tel:waiting.late')} data-testid="tel-panel-late" />
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('tel:panel.scheduled')}: <JalaliDate value={data.scheduledStart} withTime /> ·{' '}
        {t('tel:panel.join_window')}: <JalaliDate value={data.joinWindowEndsAt} withTime />
      </Typography>
      {data.waitingSince ? (
        <Alert severity="info" sx={{ mt: 1 }} data-testid="tel-panel-waiting">
          {t('tel:panel.patient_waiting')} <JalaliDate value={data.waitingSince} withTime />
        </Alert>
      ) : null}
      {start.error ? <ErrorState error={start.error} compact /> : null}
      {hostToken.error ? <ErrorState error={hostToken.error} compact /> : null}
      {downgrade.error ? <ErrorState error={downgrade.error} compact /> : null}
      {token ? (
        <Alert severity="success" sx={{ mt: 1 }} data-testid="tel-panel-token">
          {t('tel:panel.token_ready', { server: token.serverUrl })} ·{' '}
          {t('tel:panel.token_expires')} <JalaliDate value={token.expiresAt} withTime />
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {!active ? (
          <Button
            variant="contained"
            size="small"
            disabled={start.isPending || data.status === 'Completed' || data.status === 'Cancelled'}
            onClick={() => start.mutate()}
            data-testid="tel-panel-start"
          >
            {t('tel:panel.start')}
          </Button>
        ) : null}
        {active && sessionId ? (
          <Button
            size="small"
            disabled={hostToken.isPending}
            onClick={() => hostToken.mutate(sessionId)}
            data-testid="tel-panel-rejoin"
          >
            {t('tel:panel.rejoin')}
          </Button>
        ) : null}
        {active && sessionId ? (
          <Button
            size="small"
            color="warning"
            onClick={() => setEnding(true)}
            data-testid="tel-panel-end"
          >
            {t('tel:panel.end')}
          </Button>
        ) : null}
        {live && live.status !== 'Active' && live.hasTechnicalFailure === false && sessionId ? (
          <Button
            size="small"
            color="warning"
            onClick={() => setFailing(true)}
            data-testid="tel-panel-failure"
          >
            {t('tel:panel.report_failure')}
          </Button>
        ) : null}
      </Stack>
      {active && sessionId && live ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" data-testid="tel-panel-duration">
              {t('tel:panel.duration')}: {durationLabel(telNum(live.durationSeconds))}
            </Typography>
            <Typography variant="body2" data-testid="tel-panel-disconnects">
              {t('tel:panel.disconnects')}: {faDigits(telNum(live.disconnectCount))}
            </Typography>
            <TextField
              select
              size="small"
              label={t('tel:panel.downgrade')}
              value=""
              onChange={(changed) =>
                downgrade.mutate({ id: sessionId, mode: changed.target.value as TelConnectionMode })
              }
              sx={{ minWidth: 160 }}
              slotProps={{ htmlInput: { 'data-testid': 'tel-panel-downgrade' } }}
              disabled={DOWNGRADES[live.mode].length === 0 || downgrade.isPending}
            >
              {DOWNGRADES[live.mode].map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {t(`tel:mode.${mode}`)}
                </MenuItem>
              ))}
            </TextField>
            {TECHNICAL_EVENTS.map((kind) => (
              <Button
                key={kind}
                size="small"
                variant="outlined"
                disabled={event.isPending}
                onClick={() => event.mutate({ id: sessionId, kind })}
                data-testid={`tel-panel-event-${kind}`}
              >
                {t(`tel:event.${kind}`)}
              </Button>
            ))}
          </Stack>
          <SessionChat sessionId={sessionId} />
        </>
      ) : null}
      <ConfirmDialog
        open={ending}
        titleKey="tel:panel.end"
        messageKey="tel:panel.end_message"
        confirmKey="tel:panel.end"
        busy={end.isPending}
        error={end.error}
        onConfirm={() => sessionId && end.mutate(sessionId)}
        onCancel={() => setEnding(false)}
      />
      <ReasonDialog
        open={failing}
        titleKey="tel:panel.report_failure"
        messageKey="tel:panel.failure_message"
        confirmKey="tel:panel.report_failure"
        destructive
        busy={failure.isPending}
        error={failure.error}
        onConfirm={(note) => sessionId && failure.mutate({ id: sessionId, note })}
        onCancel={() => setFailing(false)}
      />
    </Paper>
  );
}

/**
 * گفتگوی متنی جلسه (US-TEL-021): پیام‌ها داده بالینی‌اند و با پایان جلسه به یک سند پرونده بایگانی می‌شوند؛
 * فایل با همان مسیر DOC بارگذاری و شناسه سندش پیوست می‌شود، پس اینجا تنها متن فرستاده می‌شود.
 */
function SessionChat({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation('tel');
  const { tenantApi, session } = useTenantSession();
  const [text, setText] = useState('');
  const chat = useQuery({
    queryKey: telKeys.chat(session?.tenantId, sessionId),
    refetchInterval: 15_000,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/telehealth/sessions/{id}/chat', { params: { path: { id: sessionId } } }),
      ),
  });
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/telehealth/sessions/{id}/chat', {
          params: { path: { id: sessionId } },
          body: { text: text.trim(), documentId: null },
        }),
      ),
    onSuccess: () => {
      setText('');
      void chat.refetch();
    },
  });
  return (
    <Stack spacing={1} sx={{ mt: 1.5 }} data-testid="tel-panel-chat">
      <Typography variant="subtitle2">{t('tel:panel.chat')}</Typography>
      {chat.error ? <ErrorState error={chat.error} compact /> : null}
      {(chat.data ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="tel-panel-chat-empty">
          {t('tel:panel.chat_empty')}
        </Typography>
      ) : (
        (chat.data ?? []).map((message) => (
          <Typography key={message.id} variant="body2" data-testid="tel-panel-chat-message">
            <strong>{t(`tel:role.${message.role}`)}</strong> · <JalaliDate value={message.sentAt} withTime />
            {message.text ? ` — ${message.text}` : ''}
            {message.documentId ? ` — ${t('tel:panel.chat_file')}` : ''}
          </Typography>
        ))
      )}
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          value={text}
          label={t('tel:panel.chat_text')}
          onChange={(changed) => setText(changed.target.value)}
          slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'tel-panel-chat-input' } }}
        />
        <Button
          size="small"
          variant="contained"
          disabled={text.trim() === '' || send.isPending}
          onClick={() => send.mutate()}
          data-testid="tel-panel-chat-send"
        >
          {t('tel:panel.chat_send')}
        </Button>
      </Stack>
      {send.error ? <ErrorState error={send.error} compact /> : null}
    </Stack>
  );
}
